import * as Tone from "tone";

export type WaveType = "sine" | "square" | "sawtooth" | "triangle";
export type FilterType = "off" | "lowpass" | "highpass";

export interface OscillatorSettings {
  id: string;
  waveType: WaveType;
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  volume: number;
  transpose: number; // semitones
  enabled: boolean;
  filterType: FilterType;
  filterFrequency: number; // Hz
  filterQ: number;
}

export const defaultOscillatorSettings: OscillatorSettings = {
  id: "osc-1",
  waveType: "sine",
  attack: 0.01,
  decay: 0.1,
  sustain: 0.5,
  release: 0.5,
  volume: -6,
  transpose: 0,
  enabled: true,
  filterType: "off",
  filterFrequency: 2000,
  filterQ: 1,
};

// ── Internal synth registry ──────────────────────────────────────────────────

interface SynthEntry {
  polySynth: Tone.PolySynth;
  filter: Tone.Filter;
  transpose: number;
  enabled: boolean;
}

const synthData = new Map<string, SynthEntry>();

function applyToSynth(s: Tone.PolySynth, settings: OscillatorSettings): void {
  s.set({
    oscillator: { type: settings.waveType },
    envelope: {
      attack: settings.attack,
      decay: settings.decay,
      sustain: settings.sustain,
      release: settings.release,
    },
  });
  s.volume.value = settings.volume;
}

function applyToFilter(f: Tone.Filter, settings: OscillatorSettings): void {
  const type = settings.filterType ?? "off";
  if (type === "off") {
    f.type = "allpass";
  } else {
    f.type = type;
    f.frequency.value = settings.filterFrequency ?? 2000;
    f.Q.value = settings.filterQ ?? 1;
  }
}

function createSynth(settings: OscillatorSettings): { polySynth: Tone.PolySynth; filter: Tone.Filter } {
  const filter = new Tone.Filter().toDestination();
  const polySynth = new Tone.PolySynth(Tone.Synth).connect(filter);
  applyToSynth(polySynth, settings);
  applyToFilter(filter, settings);
  return { polySynth, filter };
}

function safeTranspose(note: string, semitones: number): string {
  if (!semitones) return note;
  return Tone.Frequency(note).transpose(semitones).toNote() as string;
}

// Initialise with the default oscillator so audio works before the page loads
const { polySynth: _defaultPolySynth, filter: _defaultFilter } = createSynth(defaultOscillatorSettings);
synthData.set(defaultOscillatorSettings.id, {
  polySynth: _defaultPolySynth,
  filter: _defaultFilter,
  transpose: defaultOscillatorSettings.transpose,
  enabled: true,
});

// ── Public API ───────────────────────────────────────────────────────────────

/** Replace all synths with those described by the given settings array. */
export function initSynths(settingsArray: OscillatorSettings[]): void {
  synthData.forEach(e => { e.polySynth.dispose(); e.filter.dispose(); });
  synthData.clear();
  settingsArray.forEach(settings => {
    const transpose = settings.transpose ?? 0;
    const enabled = settings.enabled !== false;
    const { polySynth, filter } = createSynth({ ...settings, transpose, enabled });
    synthData.set(settings.id, { polySynth, filter, transpose, enabled });
  });
}

export function addSynth(settings: OscillatorSettings): void {
  if (!synthData.has(settings.id)) {
    const { polySynth, filter } = createSynth(settings);
    synthData.set(settings.id, {
      polySynth,
      filter,
      transpose: settings.transpose ?? 0,
      enabled: settings.enabled !== false,
    });
  }
}

export function removeSynth(id: string): void {
  const entry = synthData.get(id);
  if (entry) {
    entry.polySynth.dispose();
    entry.filter.dispose();
    synthData.delete(id);
  }
}

export function updateSynth(settings: OscillatorSettings): void {
  const entry = synthData.get(settings.id);
  if (entry) {
    applyToSynth(entry.polySynth, settings);
    applyToFilter(entry.filter, settings);
    entry.transpose = settings.transpose ?? 0;
    entry.enabled = settings.enabled !== false;
  }
}

export function triggerAttackAll(note: string, time: number): void {
  synthData.forEach(({ polySynth, transpose, enabled }) => {
    if (!enabled) return;
    polySynth.triggerAttack(safeTranspose(note, transpose), time);
  });
}

export function triggerReleaseAll(note: string, time: number): void {
  synthData.forEach(({ polySynth, transpose, enabled }) => {
    if (!enabled) return;
    polySynth.triggerRelease(safeTranspose(note, transpose), time);
  });
}

export function triggerAttackReleaseAll(note: string, duration: number, time: number): void {
  synthData.forEach(({ polySynth, transpose, enabled }) => {
    if (!enabled) return;
    polySynth.triggerAttackRelease(safeTranspose(note, transpose), duration, time);
  });
}

export function releaseAllSynths(): void {
  synthData.forEach(({ polySynth }) => polySynth.releaseAll());
}
